import * as THREE from 'three'

export type NormalPlane = 'x' | 'y' | 'z'

export type Point = 'a' | 'b' | 'c' | 1 | 2 | 3

type PlanarMap = {
  [key in NormalPlane]: {
    x: NormalPlane
    y: NormalPlane
  }
}

type PlanarMirrorMap = {
  [key in NormalPlane]: {
    x: boolean
    y: boolean
  }
}

interface XYNumber {
  x: number
  y: number
}

export const pointToIndex = (p: Point) =>
  typeof p === 'string'
    ? p.charCodeAt(0) - 97 // a = 97, b = 89, c = 99
    : p - 1

export class PlanarUVBuilder {
  private geometry: THREE.Geometry

  private axyz: THREE.Vector3
  private bxyz: THREE.Vector3
  private cxyz: THREE.Vector3

  private plane: NormalPlane
  private referencePlane: NormalPlane

  private planeOffset: XYNumber | null = null
  private planeRange: XYNumber | null = null
  private planeCorrection: XYNumber = {
    x: 1,
    y: 1,
  }

  private _auv: THREE.Vector2 | null = null
  private _buv: THREE.Vector2 | null = null
  private _cuv: THREE.Vector2 | null = null

  get auv() {
    return this._auv
  }

  get buv() {
    return this._buv
  }

  get cuv() {
    return this._cuv
  }

  private x: NormalPlane = 'x'
  private y: NormalPlane = 'y'

  private mirrorX = false
  private mirrorY = false

  private boundingBox: THREE.Box2 | null = null

  private static planarMap: PlanarMap = {
    z: {
      x: 'x',
      y: 'y',
    },
    y: {
      x: 'x',
      y: 'z',
    },
    x: {
      x: 'z',
      y: 'y',
    },
  }

  private static planarMirrorMap: PlanarMirrorMap = {
    z: {
      x: false,
      y: false,
    },
    y: {
      x: false,
      y: true,
    },
    x: {
      x: false,
      y: false,
    },
  }

  constructor(
    geometry: THREE.Geometry,
    a: number,
    b: number,
    c: number,
    plane: NormalPlane,
    referencePlane: NormalPlane = 'z'
  ) {
    this.geometry = geometry

    this.axyz = this.geometry.vertices[a]
    this.bxyz = this.geometry.vertices[b]
    this.cxyz = this.geometry.vertices[c]

    this.plane = plane
    this.referencePlane = referencePlane

    this.x = PlanarUVBuilder.planarMap[this.plane].x
    this.y = PlanarUVBuilder.planarMap[this.plane].y

    this.mirrorX = PlanarUVBuilder.planarMirrorMap[this.plane].x
    this.mirrorY = PlanarUVBuilder.planarMirrorMap[this.plane].y

    this.loadPlaneOffset()
    this.loadPlaneRange()
    this.loadPlaneCorrection()

    this.generateUVs()

    this.regenerateBoundingBox()
  }

  private generateUVs() {
    if (this.planeOffset === null) {
      throw new Error('Failed to initialize planar UV builder plane offset.')
    }

    if (this.planeRange === null) {
      throw new Error('Failed to initialize planar UV builder plane range.')
    }

    this._auv = new THREE.Vector2(
      ((this.axyz[this.x] + this.planeOffset.x) / this.planeRange.x) *
        this.planeCorrection.x,
      ((this.axyz[this.y] + this.planeOffset.y) / this.planeRange.y) *
        this.planeCorrection.y
    )

    this._buv = new THREE.Vector2(
      ((this.bxyz[this.x] + this.planeOffset.x) / this.planeRange.x) *
        this.planeCorrection.x,
      ((this.bxyz[this.y] + this.planeOffset.y) / this.planeRange.y) *
        this.planeCorrection.y
    )

    this._cuv = new THREE.Vector2(
      ((this.cxyz[this.x] + this.planeOffset.x) / this.planeRange.x) *
        this.planeCorrection.x,
      ((this.cxyz[this.y] + this.planeOffset.y) / this.planeRange.y) *
        this.planeCorrection.y
    )

    if (this.mirrorX || this.mirrorY) {
      ;[this._auv, this._buv, this._cuv].forEach((v) => {
        if (this.mirrorX) {
          v.setX(1 - v.x)
        }

        if (this.mirrorY) {
          v.setY(1 - v.y)
        }
      })
    }
  }

  public reset(excludeIndices: number[] = []) {
    return this.transform((v) => {
      v.setX(0)
      v.setY(0)
    }, excludeIndices)
  }

  public move(x: number | null, y: number | null, anchor: Point) {
    const anchorPointIndex = pointToIndex(anchor)
    const anchorPoint = [this._auv, this._buv, this._cuv][anchorPointIndex]

    if (anchorPoint === null) {
      throw new Error('Failed to initialize planar UV builder anchor point.')
    }

    const anchorOriginal = {
      x: anchorPoint.x,
      y: anchorPoint.y,
    }
    const anchorDifference = {
      x: x !== null ? x - anchorOriginal.x : null,
      y: y !== null ? y - anchorOriginal.y : null,
    }

    if (x !== null) {
      anchorPoint.setX(x)
    }

    if (y !== null) {
      anchorPoint.setY(y)
    }

    return this.transform((v, i) => {
      if (i !== anchorPointIndex) {
        if (x !== null && anchorDifference.x !== null) {
          v.setX(v.x + anchorDifference.x)
        }

        if (y !== null && anchorDifference.y !== null) {
          v.setY(v.y + anchorDifference.y)
        }
      }
    })
  }

  public rotate(angle: number, pivot: Point | THREE.Vector2) {
    const sin = Math.sin(angle)
    const cos = Math.cos(angle)

    let px: number
    let py: number

    if (pivot instanceof THREE.Vector2) {
      px = pivot.x
      py = pivot.y
    } else {
      const anchorPoint = [this._auv, this._buv, this._cuv][pointToIndex(pivot)]

      if (anchorPoint === null) {
        throw new Error('Failed to initialize planar UV builder anchor point.')
      }

      px = anchorPoint.x
      py = anchorPoint.y
    }

    return this.transform((v) => {
      const ox = v.x
      const oy = v.y

      // Point is rotation origin.
      if (ox == px && oy == py) {
        return true
      }

      // Translate point to rotation origin.
      const rx = ox - px
      const ry = oy - py

      // Rotate.
      const nx = rx * cos - ry * sin
      const ny = rx * sin + ry * cos

      // Translate point back.
      v.setX(nx + px)
      v.setY(ny + py)
    })
  }

  public offset(x: number, y: number) {
    return this.transform((v) => {
      v.setX(v.x + x)
      v.setY(v.y + y)
    })
  }

  public transform(
    transformer: (
      point: THREE.Vector2,
      index: number,
      allPoints: THREE.Vector2[]
    ) => void,
    excludeIndices: number[] = []
  ) {
    if (this._auv === null || this._buv === null || this._cuv === null) {
      throw new Error('Failed to initialize planar UV builder UVs.')
    }

    ;[this._auv, this._buv, this._cuv]
      .filter((v, i) => !excludeIndices.includes(i))
      .forEach(transformer)
    this.regenerateBoundingBox()
    return this
  }

  public toUV() {
    if (this._auv === null || this._buv === null || this._cuv === null) {
      throw new Error('Failed to initialize planar UV builder UVs.')
    }

    return [this._auv.clone(), this._buv.clone(), this._cuv.clone()]
  }

  public getBB() {
    if (this.boundingBox === null) {
      throw new Error('Failed to initialize planar UV builder bounding box.')
    }

    return this.boundingBox.clone()
  }

  public tap(tapper: (builder: PlanarUVBuilder) => void) {
    tapper(this)
    return this
  }

  private regenerateBoundingBox() {
    if (this._auv === null || this._buv === null || this._cuv === null) {
      throw new Error('Failed to initialize planar UV builder UVs.')
    }

    this.boundingBox = new THREE.Box2()
    this.boundingBox.setFromPoints([this._auv, this._buv, this._cuv])
  }

  private loadPlaneOffset() {
    this.planeOffset = PlanarUVBuilder.generatePlaneOffset(
      this.geometry,
      this.plane
    )
  }

  private loadPlaneRange() {
    this.planeRange = PlanarUVBuilder.generatePlaneRange(
      this.geometry,
      this.plane
    )
  }

  private loadPlaneCorrection() {
    // Not necessary to correct for reference plane.
    if (this.plane === this.referencePlane) {
      return
    }

    const zPlaneRange = PlanarUVBuilder.generatePlaneRange(this.geometry, 'z')

    if (this.planeRange === null) {
      throw new Error('Failed to initialize planar UV builder plane range.')
    }

    this.planeCorrection.x = this.planeRange.x / zPlaneRange.x
    this.planeCorrection.y = this.planeRange.y / zPlaneRange.y
  }

  private static generatePlaneOffset(
    geometry: THREE.Geometry,
    plane: NormalPlane
  ) {
    const bbMin = geometry.boundingBox.min
    const map = PlanarUVBuilder.planarMap[plane]

    return new THREE.Vector2(0 - bbMin[map.x], 0 - bbMin[map.y])
  }

  private static generatePlaneRange(
    geometry: THREE.Geometry,
    plane: NormalPlane
  ) {
    const bbMax = geometry.boundingBox.max
    const bbMin = geometry.boundingBox.min

    const map = PlanarUVBuilder.planarMap[plane]

    return new THREE.Vector2(
      bbMax[map.x] - bbMin[map.x],
      bbMax[map.y] - bbMin[map.y]
    )
  }
}
